import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ArrowRight, X, Sparkles, Store, Link2, CreditCard, Brain } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BUSINESS_TYPES = [
  { value: "restaurant", label: "Ristorante / Bar" },
  { value: "beauty", label: "Estetica / Parrucchiere" },
  { value: "retail", label: "Retail / Negozio" },
  { value: "services", label: "Servizi professionali" },
  { value: "hospitality", label: "Hospitality / Turismo" },
  { value: "other", label: "Altro" },
];

const STEPS = [
  {
    id: 1, title: "Benvenuto in RecenUp",
    subtitle: "Trasforma le recensioni Google in crescita reale",
    icon: Sparkles,
  },
  {
    id: 2, title: "La tua attività",
    subtitle: "Aggiungi la tua prima attività per iniziare",
    icon: Store,
  },
  {
    id: 3, title: "Connetti Google Business",
    subtitle: "Sincronizza le recensioni reali del tuo profilo",
    icon: Link2,
  },
  {
    id: 4, title: "Card NFC",
    subtitle: "Richiedi la tua prima card per raccogliere recensioni sul posto",
    icon: CreditCard,
  },
  {
    id: 5, title: "AI Insights Pro",
    subtitle: "Prova gratis 14 giorni gli insight AI sulle tue recensioni",
    icon: Brain,
  },
];

/**
 * Sprint 8 — Onboarding wizard. Shown as a full-screen overlay on first
 * login until the user completes or skips it. Each step is skippable so
 * users are never blocked; the state (`onboarding_step`) is persisted
 * server-side so the wizard resumes across sessions.
 */
export default function OnboardingWizard() {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bizType, setBizType] = useState("");
  const [bizName, setBizName] = useState("");
  const [bizAddr, setBizAddr] = useState("");
  const [trialEligible, setTrialEligible] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/onboarding/status");
        setStatus(data);
        const isDone = data?.completed || data?.skipped;
        setVisible(!isDone);
      } catch {
        setVisible(false);
      }
      try {
        const { data } = await api.get("/plan/trial-eligibility");
        setTrialEligible(data);
      } catch { /* silent */ }
    })();
  }, []);

  if (!visible || !status) return null;

  const step = Math.max(0, Math.min(status.step || 0, STEPS.length));
  const current = STEPS[step] || STEPS[0];

  const goToStep = async (nextStep, extras = {}) => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/complete-step", { step: nextStep, ...extras });
      setStatus((s) => ({ ...s, step: data.step, completed: data.completed }));
      if (data.completed) setVisible(false);
    } catch (e) {
      toast.error("Impossibile salvare il progresso");
    } finally {
      setSaving(false);
    }
  };

  const skipAll = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding/skip", {});
      setVisible(false);
    } finally { setSaving(false); }
  };

  const createBusiness = async () => {
    if (!bizName.trim()) { toast.error("Inserisci il nome dell'attività"); return; }
    setSaving(true);
    try {
      await api.post("/businesses", { name: bizName.trim(), address: bizAddr.trim() });
      await goToStep(2, { business_type: bizType || undefined });
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Impossibile creare l'attività";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const startTrial = async () => {
    setSaving(true);
    try {
      await api.post("/billing/start-trial", {});
      toast.success("Trial Pro attivato! Hai 14 giorni gratis.");
      await goToStep(5);
      // reload to refresh the plan-status-driven UI
      window.location.reload();
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Impossibile attivare il trial";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const Icon = current.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-[#0A0A0A]/90 backdrop-blur-md flex items-center justify-center p-4"
        data-testid="onboarding-wizard"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }}
          className="relative w-full max-w-2xl bg-[var(--bg-1)] border border-[var(--border-c)] rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] p-8 md:p-10"
        >
          <button
            data-testid="onboarding-skip-btn"
            onClick={skipAll}
            className="absolute top-4 right-4 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)] flex items-center gap-1 font-sans"
            style={{ transition: "color 0.2s ease" }}
          >
            <X size={12} /> Salta
          </button>

          {/* Progress */}
          <div className="flex items-center gap-2 mb-6" data-testid="onboarding-progress">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`h-1 flex-1 rounded-full ${i <= step ? "bg-[#00F2F4]" : "bg-[var(--border-c)]"}`}
                style={{ transition: "background-color 0.3s ease" }}
              />
            ))}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--fg-3)] font-sans mb-4">
            Step {Math.min(step + 1, STEPS.length)} di {STEPS.length}
          </div>

          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 rounded-xl bg-[#00F2F4]/10 border border-[#00F2F4]/30">
              <Icon size={22} className="text-[#00F2F4]" />
            </div>
            <div>
              <h2 className="font-serif text-2xl md:text-3xl text-[var(--fg-1)] tracking-tight" data-testid="onboarding-step-title">
                {current.title}
              </h2>
              <p className="mt-1 text-sm text-[var(--fg-3)]">{current.subtitle}</p>
            </div>
          </div>

          <div className="min-h-[180px] py-4">
            {step === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-[var(--fg-2)] leading-relaxed">
                  Bene, iniziamo. Che tipo di attività gestisci? Ci aiuta a personalizzare gli esempi che vedrai in dashboard.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {BUSINESS_TYPES.map((b) => (
                    <button
                      key={b.value}
                      onClick={() => setBizType(b.value)}
                      data-testid={`onboarding-biztype-${b.value}`}
                      className={`text-left text-sm px-3 py-2 rounded-lg border ${
                        bizType === b.value
                          ? "border-[#00F2F4] bg-[#00F2F4]/10 text-[#00F2F4]"
                          : "border-[var(--border-c)] text-[var(--fg-2)] hover:border-[var(--fg-3)]"
                      }`}
                      style={{ transition: "border-color 0.2s ease" }}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="space-y-3">
                <input
                  data-testid="onboarding-biz-name"
                  value={bizName} onChange={(e) => setBizName(e.target.value)}
                  placeholder="Nome dell'attività (es. Trattoria da Luigi)"
                  className="w-full bg-[var(--bg-2)] border border-[var(--border-c)] rounded-lg px-4 py-3 text-sm text-[var(--fg-1)] placeholder:text-[var(--fg-3)] focus:border-[#00F2F4] outline-none"
                />
                <input
                  data-testid="onboarding-biz-addr"
                  value={bizAddr} onChange={(e) => setBizAddr(e.target.value)}
                  placeholder="Indirizzo (opzionale)"
                  className="w-full bg-[var(--bg-2)] border border-[var(--border-c)] rounded-lg px-4 py-3 text-sm text-[var(--fg-1)] placeholder:text-[var(--fg-3)] focus:border-[#00F2F4] outline-none"
                />
              </div>
            )}
            {step === 2 && (
              <div className="space-y-3">
                <p className="text-sm text-[var(--fg-2)] leading-relaxed">
                  Collega il tuo profilo Google Business per sincronizzare recensioni reali automaticamente. Puoi anche farlo dopo dal Profilo.
                </p>
                <div className="text-xs text-[var(--fg-3)] italic">
                  Suggerimento: nel frattempo la dashboard mostra dati di esempio realistici per farti esplorare le funzioni.
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-3">
                <p className="text-sm text-[var(--fg-2)] leading-relaxed">
                  Le card NFC RecenUp aumentano il numero di recensioni del <strong className="text-[var(--fg-1)]">3-5x</strong>. Il cliente le tocca col telefono e apre direttamente la pagina recensione Google.
                </p>
                <div className="text-xs text-[var(--fg-3)]">Puoi richiederle in seguito dalla Dashboard.</div>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-3">
                {trialEligible?.eligible ? (
                  <>
                    <p className="text-sm text-[var(--fg-2)] leading-relaxed">
                      Attiva <strong className="text-[#00F2F4]">14 giorni gratis</strong> di RecenUp Pro: AI Insights, risposte suggerite, quota AI mensile e multi-location. Nessuna carta richiesta.
                    </p>
                    <ul className="space-y-1.5 text-sm text-[var(--fg-2)]">
                      <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#4A8A6E]" /> Sentiment analysis su tutte le recensioni</li>
                      <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#4A8A6E]" /> 100 utilizzi AI al mese</li>
                      <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#4A8A6E]" /> Risposte suggerite in un click</li>
                    </ul>
                  </>
                ) : (
                  <p className="text-sm text-[var(--fg-2)]">
                    {trialEligible?.reason === "already_used" && "Hai già usato il trial gratuito — puoi continuare con un piano Pro."}
                    {trialEligible?.reason === "already_pro" && "Sei già Pro. Ottimo!"}
                    {trialEligible?.reason === "already_active" && "Trial già attivo."}
                    {!trialEligible?.reason && "Sblocca RecenUp Pro per accedere alle funzioni AI e analytics avanzate."}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              onClick={skipAll} disabled={saving}
              className="text-xs text-[var(--fg-3)] hover:text-[var(--fg-1)] font-sans"
              style={{ transition: "color 0.2s ease" }}
            >
              Salta tutto
            </button>
            <div className="flex items-center gap-2">
              {step === 0 && (
                <button
                  onClick={() => goToStep(1, { business_type: bizType || undefined })}
                  disabled={saving}
                  data-testid="onboarding-next-1"
                  className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold"
                >
                  Continua <ArrowRight size={14} />
                </button>
              )}
              {step === 1 && (
                <>
                  <button onClick={() => goToStep(2)} disabled={saving}
                          className="text-xs text-[var(--fg-3)] hover:text-[var(--fg-1)] px-3 py-2 font-sans">
                    Salta
                  </button>
                  <button onClick={createBusiness} disabled={saving}
                          data-testid="onboarding-create-biz"
                          className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] disabled:opacity-60 text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold">
                    Crea attività <ArrowRight size={14} />
                  </button>
                </>
              )}
              {step === 2 && (
                <>
                  <button onClick={() => goToStep(3)} disabled={saving}
                          className="text-xs text-[var(--fg-3)] hover:text-[var(--fg-1)] px-3 py-2 font-sans">
                    Salta
                  </button>
                  <button
                    onClick={async () => { await goToStep(3); window.location.href = "/profile#gbp"; }}
                    disabled={saving}
                    data-testid="onboarding-connect-gbp"
                    className="inline-flex items-center gap-2 rounded-full border border-[#00F2F4] text-[#00F2F4] hover:bg-[#00F2F4]/10 px-5 py-2.5 text-sm font-sans font-semibold"
                  >
                    Connetti Google <ArrowRight size={14} />
                  </button>
                </>
              )}
              {step === 3 && (
                <>
                  <button onClick={() => goToStep(4)} disabled={saving}
                          className="text-xs text-[var(--fg-3)] hover:text-[var(--fg-1)] px-3 py-2 font-sans">
                    Salta
                  </button>
                  <button onClick={() => goToStep(4)} disabled={saving}
                          data-testid="onboarding-next-card"
                          className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold">
                    Ok, prosegui <ArrowRight size={14} />
                  </button>
                </>
              )}
              {step === 4 && (
                <>
                  <button onClick={() => goToStep(5)} disabled={saving}
                          className="text-xs text-[var(--fg-3)] hover:text-[var(--fg-1)] px-3 py-2 font-sans">
                    Salta
                  </button>
                  <button
                    onClick={trialEligible?.eligible ? startTrial : () => goToStep(5)}
                    disabled={saving}
                    data-testid="onboarding-start-trial"
                    className="inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] disabled:opacity-60 text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold"
                  >
                    {trialEligible?.eligible ? "Attiva 14 giorni gratis" : "Finisci"} <ArrowRight size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
